import React from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from './ModalHeader';

interface Props {
  open: boolean;
  title?: string;
  message: React.ReactNode;
  /** Dismiss button label (default "OK"). */
  closeLabel?: string;
  onClose: () => void;
}

/**
 * Minimal single-button info/alert modal. Portals to <body> (z-[120]) so it
 * escapes any transformed/scrolled ancestor (e.g. the editor drawer panel).
 * Click the backdrop or the button to dismiss.
 */
const AlertModal: React.FC<Props> = ({ open, title = 'Alert', message, closeLabel = 'OK', onClose }) => {
  // Alt+C dismisses. Claiming the layer while open is the point of the chord
  // here: it stops Alt+S/Alt+C from reaching whatever surface this sits over
  // (the Pro drawer, the quote editor drawer) instead of this alert.
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/40 p-6"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="w-full max-w-[420px] rounded-lg border border-slate-200 bg-cmp-modal shadow-xl dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader title={title} onClose={onClose} dragHandleProps={dragHandleProps} />
        <div className="px-4 py-4 text-[0.78125rem] text-slate-700 dark:text-slate-200">{message}</div>
        <div className="flex justify-end border-t border-slate-200 px-4 py-3 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-blue-700 bg-blue-600 px-3 py-1.5 text-[0.75rem] font-semibold text-white hover:bg-blue-700"
          >
            {closeLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AlertModal;
